import { serverOrigin } from './serverBase.mjs';
import { describeNetFailure, proxyStatus, isNetworkError } from './netDiagnosis.mjs';

// A function, not a constant: the shell injects the server origin at
// document start, and a constant captured at module load would race it.
const BASE = () => process.env.REACT_APP_API_URL || `${serverOrigin()}/api`;

function getToken() {
  return localStorage.getItem('voxiox_token');
}

async function request(path, options = {}) {
  const token = getToken();
  let res;
  try {
    res = await fetch(`${BASE()}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      },
      body: options.body ? JSON.stringify(options.body) : undefined
    });
  } catch (e) {
    // «Failed to fetch» names the layer that gave up, not the reason. The
    // local proxy knows which path it tried and whether the person forbade
    // the direct one; say that instead (netDiagnosis.mjs).
    if (!isNetworkError(e)) throw e;
    const err = new Error(describeNetFailure(proxyStatus()));
    err.cause = e; err.network = true;
    throw err;
  }
  // Not every response is JSON. express-rate-limit answers 429 with the plain
  // text "Too many requests", and parsing that produced
  // `SyntaxError: Unexpected token 'T'` — an error that names the parser and
  // hides the cause, which is exactly what it did here.
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch (e) { /* not JSON */ }

  if (!res.ok) {
    const err = new Error(
      (data && data.error)
      || (res.status === 429 ? 'Слишком много запросов — попробуйте через минуту' : null)
      || (text ? text.slice(0, 200) : `HTTP ${res.status}`),
    );
    err.reason = data?.reason;
    err.status = res.status;
    err.body = text;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  login: (username, password) => request('/auth/login', { method: 'POST', body: { username, password } }),
  me: () => request('/auth/me'),
  setDisplayName: (display_name) => request('/auth/display-name', { method: 'PUT', body: { display_name } }),
  sipCreds: () => request('/auth/sip-creds'),
  telegramAuth: (telegram_id, telegram_username) => request('/auth/telegram', { method: 'POST', body: { telegram_id, telegram_username } }),
  exchangeCode: (code) => request('/auth/exchange-code', { method: 'POST', body: { code } }),
  getTelegramLinkToken: () => request('/telegram/link-token', { method: 'POST' }),

  // Calls
  createLink: (label, expires_hours, max_uses, max_participants) =>
    request('/calls/links', { method: 'POST', body: { label, expires_hours, max_uses, max_participants } }),
  getGuestCreds: (link_code) => request('/calls/guest-creds', { method: 'POST', body: { link_code } }),
  getLinks: () => request('/calls/links'),
  resolveLink: (code) => request(`/calls/links/${code}`),
  deleteLink: (code) => request(`/calls/links/${code}`, { method: 'DELETE' }),
  startCall: (link_code, callee_extension) => request('/calls/start', { method: 'POST', body: { link_code, callee_extension } }),
  updateCallStatus: (uuid, status) => request(`/calls/status/${uuid}`, { method: 'PATCH', body: { status } }),
  getHistory: (page = 1) => request(`/calls/history?page=${page}`),
  getUsers: () => request('/calls/users'),
  roomNotify: (code) => request('/calls/room-notify', { method: 'POST', body: { link_code: code } }),
  getActiveRooms: () => request('/calls/active-rooms'),
  clearActiveRoom: (code) => request(`/calls/active-rooms/${code}`, { method: 'DELETE' }),

  // Admin
  adminGetUsers: (params = {}) => request('/admin/users?' + new URLSearchParams(params)),
  adminGetUser: (id) => request(`/admin/users/${id}`),
  adminPatchUser: (id, body) => request(`/admin/users/${id}`, { method: 'PATCH', body }),
  adminDisableUser: (id) => request(`/admin/users/${id}/disable`, { method: 'POST' }),
  adminEnableUser: (id) => request(`/admin/users/${id}/enable`, { method: 'POST' }),
  adminResetPassword: (id) => request(`/admin/users/${id}/reset-password`, { method: 'POST' }),
  // No arguments: the server makes the login and the password. The browser
  // used to generate both, which put every account's strength at the mercy of
  // whatever entropy that browser had.
  adminCreateUser: (body = {}) => request('/admin/users', { method: 'POST', body }),
  adminAudit: (params = {}) => request('/admin/audit?' + new URLSearchParams(params)),
  adminBulk: (action, ids) => request('/admin/users/bulk', { method: 'POST', body: { action, ids } }),
  adminDeleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),

  // Friends (ADR-013: consent-based contacts)
  getFriends: () => request('/friends'),
  getFriendRequests: () => request('/friends/requests'),
  searchUsers: (q) => request(`/friends/search?q=${encodeURIComponent(q)}`),
  sendFriendRequest: (userId) => request('/friends/request', { method: 'POST', body: { userId } }),
  acceptFriendRequest: (requesterId) => request(`/friends/${requesterId}/accept`, { method: 'POST' }),
  declineFriendRequest: (requesterId) => request(`/friends/${requesterId}/decline`, { method: 'POST' }),
  removeFriend: (userId) => request(`/friends/${userId}`, { method: 'DELETE' }),
  blockUser: (userId) => request(`/friends/${userId}/block`, { method: 'POST' }),
  unblockUser: (userId) => request(`/friends/${userId}/unblock`, { method: 'POST' }),
  getBlocked: () => request('/friends/blocked'),

  // X3DH key distribution (ADR-016). Public keys only — nothing here is secret,
  // and nothing here is trusted until the client checks the signature against
  // a TOFU-pinned identity.
  publishBundle: (bundle) => request('/keys/bundle', { method: 'POST', body: bundle }),
  uploadOneTimePreKeys: (deviceId, preKeys) => request('/keys/onetime', { method: 'POST', body: { deviceId, preKeys } }),
  oneTimeRemaining: (deviceId) => request(`/keys/onetime/${deviceId}`),
  // Listing devices consumes nothing. Fetching a bundle consumes a one-time
  // prekey, so it is asked for only when a session has to be created.
  listDevices: (userId) => request(`/keys/devices/${userId}`),
  // The append-only record of every identity key the server was told about.
  // Asked for from where we stopped last time: the client verifies that the
  // chain CONTINUES, which is a different question from what is true now.
  keyLog: (since = 0) => request(`/keys/log?since=${since}`),
  // The account's own statement of which devices are its own. The server
  // stores this and hands it back; it cannot produce one, because it is signed
  // by a key the server has never held.
  /** Signed configuration (docs/protocol/config-v1.md); null when CORE publishes none. */
  getConfig: async (kind) => {
    const r = await fetch(`${BASE()}/config/${kind}`, { cache: 'no-store' });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`config ${kind}: HTTP ${r.status}`);
    return r.json();
  },
  getDeviceList: (userId, since = null) =>
    request(`/keys/device-list/${userId}${since != null ? `?since=${Number(since)}` : ''}`),
  putDeviceList: (body) => request('/keys/device-list', { method: 'POST', body }),
  getBundles: (userId, deviceIds = null) =>
    request(`/keys/bundles/${userId}${deviceIds?.length ? `?devices=${encodeURIComponent(deviceIds.join(','))}` : ''}`),
  revokeDevice: (deviceId) => request(`/keys/revoke/${deviceId}`, { method: 'POST' }),

  // Identity keys (ADR-014) — directory for fingerprints; trust lives in the
  // local TOFU pin, not in these responses.
  publishIdentityKey: (publicKey, deviceLabel) => request('/identity/key', { method: 'POST', body: { publicKey, deviceLabel } }),
  getMyIdentityKeys: () => request('/identity/me'),
  getIdentityKeys: (userId) => request(`/identity/${userId}`),

  // Messages
  // Cursors, not offsets: pages shift under the reader as new messages arrive.
  // deviceId selects this device's copies of encrypted messages; omitting it
  // returns only the plaintext era, which is what an older bundle wants.
  getMessages: (userId, page = 1, deviceId = null) =>
    request(`/messages/${userId}?page=${page}${deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : ''}`),
  getMessagesAfter: (userId, afterId, deviceId = null) =>
    request(`/messages/${userId}?after=${afterId}${deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : ''}`),
  getMessagesBefore: (userId, beforeId, deviceId = null) =>
    request(`/messages/${userId}?before=${beforeId}${deviceId ? `&deviceId=${encodeURIComponent(deviceId)}` : ''}`),
  getUnreadCounts: () => request('/messages/unread'),
  getLastMessages: (deviceId = null) =>
    request(`/messages/last-messages${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ''}`),

  // WebAuthn
  webauthnRegisterOptions: () => request('/auth/webauthn/register-options', { method: 'POST' }),
  webauthnRegister: (credential) => request('/auth/webauthn/register', { method: 'POST', body: credential }),
  webauthnLoginOptions: (username) => request('/auth/webauthn/login-options', { method: 'POST', body: { username } }),
  webauthnLogin: (credential) => request('/auth/webauthn/login', { method: 'POST', body: credential }),
  webauthnHasKeys: (username) => request(`/auth/webauthn/has-keys?username=${encodeURIComponent(username)}`),

  // Settings
  getSettings: () => request('/settings'),
  updateSettings: (data) => request('/settings', { method: 'PUT', body: data }),

  // Push notifications
  getVapidKey: () => request('/push/vapid-key').then(r => r.key),
  pushSubscribe: (subscription) => request('/push/subscribe', { method: 'POST', body: subscription }),
  pushUnsubscribe: (endpoint) => request('/push/unsubscribe', { method: 'POST', body: { endpoint } }),

  // APNs device token (iOS VoIP push)
  // kind 'voip' (PushKit/calls) or 'alert' (messages, contact requests) —
  // separate APNs channels, separate tokens.
  // deviceId binds the token to the device it reaches. Without it the server
  // can only ask whether the PERSON is looking at the app, and answers for a
  // phone in a pocket with the state of a laptop tab.
  registerAPNsToken: (deviceToken, kind = 'voip', deviceId = null) => request('/push/register-apns', { method: 'POST', body: { deviceToken, kind, deviceId } }),
  unregisterAPNsToken: (deviceToken) => request('/push/unregister-apns', { method: 'POST', body: { deviceToken } }),

  // Files
  uploadFile: async (file, duration) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    if (duration) formData.append('duration', String(duration));
    const res = await fetch(`${BASE()}/files/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return { fileId: data.id, ...data };
  },
  /**
   * Upload file with progress tracking (uses XMLHttpRequest for upload.onprogress).
   * @param {File|Blob} file
   * @param {function(number)} onProgress - Called with percent (0-100)
   * @param {number} [duration] - Audio duration in seconds (optional)
   * @returns {Promise<{fileId: number, ...}>}
   */
  uploadFileWithProgress: (file, onProgress, duration) => {
    return new Promise((resolve, reject) => {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file, file.name || 'image.jpg');
      if (duration) formData.append('duration', String(duration));

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE()}/files/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ fileId: data.id, ...data });
          } else {
            reject(new Error(data.error || 'Upload failed'));
          }
        } catch (e) {
          reject(new Error('Upload failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });
  },

  getFileUrl: (fileId) => `${BASE()}/files/${fileId}`,

  /**
   * Seal a file on the device and upload the ciphertext (SEC-04, §7в).
   *
   * The server receives an anonymous blob — generic name, generic MIME —
   * and can open none of it. What the recipient needs to undo that travels
   * back to the caller and belongs INSIDE the message envelope: the random
   * key and the real metadata. Returns { fileId, meta } where meta is the
   * envelope-ready `{ id, k, name, mime, size, dur }`.
   */
  uploadSealed: async (file, { onProgress = null, duration = null } = {}) => {
    const { sealFile } = await import('./fileCrypto.mjs');
    const bytes = new Uint8Array(await file.arrayBuffer());
    const { sealed, keyB64 } = sealFile(bytes);
    const blob = new File([sealed], 'blob.bin', { type: 'application/octet-stream' });
    const up = onProgress
      ? await api.uploadFileWithProgress(blob, onProgress)
      : await api.uploadFile(blob);
    return {
      fileId: up.fileId,
      meta: {
        id: up.fileId,
        k: keyB64,
        name: file.name || 'file',
        mime: file.type || 'application/octet-stream',
        size: bytes.length,
        ...(duration ? { dur: duration } : {}),
      },
    };
  },
};
